import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Stack,
  alpha,
} from "@mui/material";

import PageContainer from "../layouts/PageContainer";
import { getDashboardStats } from "../redux/actions/dashboardActions";
import { openTab } from "../redux/actions/workspaceActions";
import {
  getEntityMeta,
  getSidebarItemByEntity,
} from "../helpers/dashboardHelpers";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { EntityType } from "../shared/enums/EntityType";

// Widgets matching your component directory
import AcademicPerformanceWidget from "../components/dashboard/widgets/AcademicPerformanceWidget";
import AcademicYearWidget from "../components/dashboard/widgets/AcademicYearWidget";
import AnnouncementOverviewWidget from "../components/dashboard/widgets/AnnouncementOverviewWidget";
import AssessmentWidget from "../components/dashboard/widgets/AssessmentWidget";
import AttendanceWidget from "../components/dashboard/widgets/AttendanceWidget";
import AuditLogWidget from "../components/dashboard/widgets/AuditLogWidget";
import BankTransactionStagingWidget from "../components/dashboard/widgets/BankTransactionStagingWidget";
import ClassroomWidget from "../components/dashboard/widgets/ClassroomWidget";
import CurriculumFulfillmentWidget from "../components/dashboard/widgets/CurriculumFulfillmentWidget";
import EnrollmentWidget from "../components/dashboard/widgets/EnrollmentWidget";
import FeeStructureOverviewWidget from "../components/dashboard/widgets/FeeStructureOverviewWidget";
import GenderPieChartWidget from "../components/dashboard/widgets/GenderPieChartWidget";
import InventoryLogWidget from "../components/dashboard/widgets/InventoryLogWidget";
import InventoryOverviewWidget from "../components/dashboard/widgets/InventoryOverviewWidget";
import NewsletterWidget from "../components/dashboard/widgets/NewsletterWidget";
import NotificationOverviewWidget from "../components/dashboard/widgets/NotificationOverviewWidget";
import PaymentWidget from "../components/dashboard/widgets/PaymentWidget";
import PurchaseOrderOverviewWidget from "../components/dashboard/widgets/PurchaseOrderOverviewWidget";
import SimpleStatWidget from "../components/dashboard/widgets/SimpleStatWidget";
import SmsOutboxWidget from "../components/dashboard/widgets/SmsOutboxWidget";
import SmsTemplateWidget from "../components/dashboard/widgets/SmsTemplateWidget";
import StudentFeeLedgerWidget from "../components/dashboard/widgets/StudentFeeLedgerWidget";
import StudentTermReportWidget from "../components/dashboard/widgets/StudentTermReportWidget";
import SupplierOverviewWidget from "../components/dashboard/widgets/SupplierOverviewWidget";
import TimeslotOverviewWidget from "../components/dashboard/widgets/TimeslotOverviewWidget";
import TimetableSlotOverviewWidget from "../components/dashboard/widgets/TimetableSlotOverviewWidget";
import TimetableWidget from "../components/dashboard/widgets/TimetableWidget";
import TransitionRecordWidget from "../components/dashboard/widgets/TransitionRecordWidget";
import UserStatusWidget from "../components/dashboard/widgets/UserStatusWidget";
import VoteHeadWidget from "../components/dashboard/widgets/VoteHeadWidget";
import PageError from "../components/common/PageError";

const DashboardPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { stats, loadingStats, errorLoadingStats } = useAppSelector(
    (state) => state.dashboard,
  );
  const { userInfo } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(getDashboardStats(10));
  }, [dispatch]);

  // --- TABBED WORKSPACE NAVIGATION ---
  const handleSuggestionClick = (entityKey: string): void => {
    const sidebarItem = getSidebarItemByEntity(entityKey);

    if (sidebarItem && sidebarItem.componentKey) {
      dispatch(
        openTab({
          id: sidebarItem.path,
          title: sidebarItem.title,
          path: sidebarItem.path,
          componentKey: sidebarItem.componentKey,
        }),
      );
      navigate(sidebarItem.path);
    }
  };

  if (loadingStats) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorLoadingStats) {
    return (
      <PageContainer title="Dashboard">
        <PageError
          error={errorLoadingStats}
          onRetry={() => dispatch(getDashboardStats())}
        />
      </PageContainer>
    );
  }

  const { suggestedLinks = [], metrics = {} } = stats || {};

  // Widget Router: Maps each EntityType enum strongly to its widget
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderWidget = (entityKey: EntityType, data: any): React.ReactNode => {
    switch (entityKey) {
      case EntityType.TEACHER:
      case EntityType.STUDENT:
        return <GenderPieChartWidget entityName={entityKey} data={data} />;

      case EntityType.USER:
        return <UserStatusWidget data={data} />;

      case EntityType.ATTENDANCE_RECORD:
        return <AttendanceWidget data={data} />;

      case EntityType.ENROLLMENT:
        return <EnrollmentWidget data={data} />;

      case EntityType.STUDENT_FEE_LEDGER:
        return <StudentFeeLedgerWidget data={data} />;

      case EntityType.ASSESSMENT:
        return <AssessmentWidget data={data} />;

      case EntityType.CURRICULUM_REQUIREMENT:
        return <CurriculumFulfillmentWidget data={data} />;

      case EntityType.REPORT_CARD:
        return <AcademicPerformanceWidget data={data} />;

      case EntityType.ACADEMIC_YEAR:
        return <AcademicYearWidget data={data} />;

      case EntityType.TIMETABLE:
        return <TimetableWidget data={data} />;

      case EntityType.VOTE_HEAD:
        return <VoteHeadWidget data={data} />;

      case EntityType.TRANSITION_RECORD:
        return <TransitionRecordWidget data={data} />;

      case EntityType.FEE_STRUCTURE:
        return <FeeStructureOverviewWidget data={data} />;

      case EntityType.TIMESLOT:
        return <TimeslotOverviewWidget data={data} />;

      case EntityType.TIMETABLE_SLOT:
        return <TimetableSlotOverviewWidget data={data} />;

      case EntityType.CLASSROOM:
        return <ClassroomWidget data={data} />;

      case EntityType.INVENTORY_ITEM:
      case EntityType.STOCK_LEVEL:
        return <InventoryOverviewWidget data={data} />;

      case EntityType.INVENTORY_LOG:
        return <InventoryLogWidget data={data} />;

      case EntityType.SUPPLIER:
        return <SupplierOverviewWidget data={data} />;

      case EntityType.ANNOUNCEMENT:
        return <AnnouncementOverviewWidget data={data} />;

      case EntityType.NOTIFICATION:
        return <NotificationOverviewWidget data={data} />;

      case EntityType.SMS_OUTBOX:
        return <SmsOutboxWidget data={data} />;

      case EntityType.SMS_TEMPLATE:
        return <SmsTemplateWidget data={data} />;

      case EntityType.PURCHASE_ORDER:
        return <PurchaseOrderOverviewWidget data={data} />;

      case EntityType.PAYMENT:
      case EntityType.UNALLOCATED_PAYMENT:
        return <PaymentWidget data={data} />;

      case EntityType.STUDENT_TERM_REPORT:
        return <StudentTermReportWidget data={data} />;

      case EntityType.NEWSLETTER:
        return <NewsletterWidget data={data} />;

      case EntityType.BANK_TRANSACTION_STAGING:
        return <BankTransactionStagingWidget data={data} />;

      case EntityType.AUDIT_LOG:
        return <AuditLogWidget data={data} />;

      // Fallback for simple-count entities (PARENT_GUARDIAN, BRANCH, SCHOOL, ROLE, etc.)
      default:
        return <SimpleStatWidget entityType={entityKey} data={data} />;
    }
  };

  return (
    <PageContainer
      title={`Welcome back, ${userInfo?.firstName || "User"}!`}
      subtitle="Here is an overview of your activity and relevant statistics."
    >
      {/* 1. HORIZONTALLY SCROLLABLE SUGGESTED LINKS */}
      {suggestedLinks.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "bold", color: "text.secondary", mb: 2 }}
          >
            Quick Actions & Suggested Modules
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { display: "none" },
              msOverflowStyle: "none",
              scrollbarWidth: "none",
            }}
          >
            {suggestedLinks.map((entityKey: EntityType) => {
              const meta = getEntityMeta(entityKey);
              const hasRoute = !!getSidebarItemByEntity(entityKey);
              const Icon = meta.icon;

              return (
                <Paper
                  key={entityKey}
                  elevation={0}
                  onClick={() => hasRoute && handleSuggestionClick(entityKey)}
                  sx={{
                    minWidth: 180,
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    cursor: hasRoute ? "pointer" : "default",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": hasRoute
                      ? {
                          borderColor: meta.color,
                          boxShadow: `0 4px 12px ${alpha(meta.color, 0.2)}`,
                          transform: "translateY(-2px)",
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: `${meta.color}15`,
                      color: meta.color,
                      display: "flex",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: "bold" }}
                    noWrap
                  >
                    {meta.title}
                  </Typography>
                </Paper>
              );
            })}
          </Stack>
        </Box>
      )}

      {/* 2. DYNAMIC METRICS GRID */}
      {Object.keys(metrics).length > 0 ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {Object.entries(metrics).map(([entityKey, data]) => (
            <Box key={entityKey}>
              {renderWidget(entityKey as EntityType, data)}
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <Typography sx={{ color: "text.secondary" }}>
            No statistics available yet.
          </Typography>
        </Box>
      )}
    </PageContainer>
  );
};

export default DashboardPage;
